import React, { useState, useEffect, useMemo, useCallback } from "react";
import { RefreshCw, Info, ArrowDownUp } from "lucide-react";
import MKTBanner from "./MKTBanner.jsx";
import Footer from "./Footer.jsx";
import usdtIcon from "../assets/icons/usdt.png";
import ethIcon from "../assets/icons/eth.png";
import usdcIcon from "../assets/icons/usdc.png";
import bnbIcon from "../assets/icons/bnb.png";
import btcIcon from "../assets/icons/btc.png";
import solIcon from "../assets/icons/sol.png";
import usdIcon from "../assets/icons/usd.png";
import eurIcon from "../assets/icons/eur.png";
import brlIcon from "../assets/icons/brl.png";
import aedIcon from "../assets/icons/aed.png";
import { exchangeRateService } from "./../services/exchangeRateService";
import { NumericFormat } from "react-number-format";

const CoinValueWebsite = () => {
  const [cryptoData, setCryptoData] = useState({});
  const [loading, setLoading] = useState(true);
  const [fromCurrency, setFromCurrency] = useState("bitcoin");
  const [toCurrency, setToCurrency] = useState("usd");
  const [dataSource, setDataSource] = useState("CoinGecko");
  const [amount, setAmount] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [conversionMode, setConversionMode] = useState("crypto-to-fiat"); // New state for tracking conversion direction

  // Memoize static lists to avoid recreating on every render
  const cryptoList = useMemo(() => [
    { id: "bitcoin", name: "Bitcoin", symbol: "BTC", icon: btcIcon },
    { id: "ethereum", name: "Ethereum", symbol: "ETH", icon: ethIcon },
    { id: "binancecoin", name: "BNB", symbol: "BNB", icon: bnbIcon },
    { id: "solana", name: "Solana", symbol: "SOL", icon: solIcon },
    { id: "tether", name: "USDT", symbol: "USDT", icon: usdtIcon },
    { id: "usd-coin", name: "USDC", symbol: "USDC", icon: usdcIcon },
  ], []);

  const fiatList = useMemo(() => [
    { id: "usd", name: "US Dollar", symbol: "$", icon: usdIcon },
    { id: "brl", name: "Brazilian Real", symbol: "R$", icon: brlIcon },
    { id: "eur", name: "Euro", symbol: "€", icon: eurIcon },
    { id: "aed", name: "UAE Dirham", symbol: "د.إ", icon: aedIcon },
  ], []);

  const allCurrencies = useMemo(() => [...cryptoList, ...fiatList], [cryptoList, fiatList]);

  const fetchCryptoData = useCallback(async () => {
    setLoading(true);

    const res = await exchangeRateService.fetchExchangeRate();

    const data = res.data;
    const source = res.source;
    setDataSource(source);

    const coinData = {
      bitcoin: {
        usd: data.bitcoin.usd || 0,
        eur: data.bitcoin.eur || 0,
        brl: data.bitcoin.brl || 0,
        aed: data.bitcoin.aed || 0,
      },
      ethereum: {
        usd: data.ethereum.usd || 0,
        eur: data.ethereum.eur || 0,
        brl: data.ethereum.brl || 0,
        aed: data.ethereum.aed || 0,
      },
      solana: {
        usd: data.solana.usd || 0,
        eur: data.solana.eur || 0,
        brl: data.solana.brl || 0,
        aed: data.solana.aed || 0,
      },
      binancecoin: {
        usd: data.binancecoin.usd || 0,
        eur: data.binancecoin.eur || 0,
        brl: data.binancecoin.brl || 0,
        aed: data.binancecoin.aed || 0,
      },
      tether: {
        usd: data.tether.usd || 0,
        eur: data.tether.eur || 0,
        brl: data.tether.brl || 0,
        aed: data.tether.aed || 0,
      },
      "usd-coin": {
        usd: data["usd-coin"].usd || 0,
        eur: data["usd-coin"].eur || 0,
        brl: data["usd-coin"].brl || 0,
        aed: data["usd-coin"].aed || 0,
      },
    };

    setCryptoData(coinData);
    setLastUpdated(new Date().toLocaleTimeString());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCryptoData();
    // Refresh data every 60 seconds
    const interval = setInterval(fetchCryptoData, 60000);
    return () => clearInterval(interval);
  }, [fetchCryptoData]);

  // Calculate conversion for a specific currency - Memoized to avoid recalculation
  const calculateConversion = useCallback((targetCurrency) => {
    const fromId = fromCurrency;
    const toId = targetCurrency || toCurrency;

    // For crypto to fiat or crypto to crypto
    if (conversionMode === "crypto-to-fiat") {
      if (cryptoList.some((c) => c.id === fromId)) {
        if (fiatList.some((f) => f.id === toId)) {
          // Crypto to fiat
          return amount * (cryptoData[fromId]?.[toId] || 0);
        } else {
          // Crypto to crypto
          const fromUsdValue = cryptoData[fromId]?.usd || 0;
          const toUsdValue = cryptoData[toId]?.usd || 0;
          return toUsdValue ? (amount * fromUsdValue) / toUsdValue : 0;
        }
      }
    }
    // For fiat to crypto
    else if (conversionMode === "fiat-to-crypto") {
      // If source is fiat and target is crypto
      if (
        fiatList.some((f) => f.id === fromId) &&
        cryptoList.some((c) => c.id === toId)
      ) {
        // Get USD value of target crypto
        const cryptoUsdValue = cryptoData[toId]?.usd || 0;

        if (!cryptoUsdValue) return 0;

        // For USD to crypto direct conversion
        if (fromId === "usd") {
          return amount / cryptoUsdValue;
        }

        // For other fiats, use bitcoin as reference to get USD equivalent
        const referenceId = "bitcoin";

        // Calculate the USD equivalent of the source fiat amount
        // Example: If 1 BTC = 50000 USD and 1 BTC = 45000 EUR
        // Then 1 EUR = 50000/45000 USD = 1.11 USD
        const fromFiatToUsd =
          cryptoData[referenceId]?.usd / cryptoData[referenceId]?.[fromId];

        // Convert from source fiat to USD, then from USD to crypto
        return (amount * fromFiatToUsd) / cryptoUsdValue;
      }
    }

    return 0;
  }, [fromCurrency, toCurrency, cryptoData, amount, conversionMode, cryptoList, fiatList]);

  const handleRefresh = useCallback(() => {
    fetchCryptoData();
  }, [fetchCryptoData]);

  // Toggle between crypto-to-fiat and fiat-to-crypto conversion modes
  const toggleConversionMode = () => {
    // Reset amount when switching modes
    setAmount(1);

    if (conversionMode === "crypto-to-fiat") {
      setConversionMode("fiat-to-crypto");
      // Set default currencies for fiat-to-crypto
      setFromCurrency("usd");
      setToCurrency("bitcoin");
    } else {
      setConversionMode("crypto-to-fiat");
      // Set default currencies for crypto-to-fiat
      setFromCurrency("bitcoin");
      setToCurrency("usd");
    }
  };

  // Format number with proper decimal places - Memoized
  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined) return "N/A";
    if (num === 0) return "0";

    if (num < 0.01) return num.toFixed(6);
    if (num < 1) return num.toFixed(4);
    if (num < 10) return num.toFixed(3);
    if (num < 1000) return num.toFixed(2);

    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }, []);

  // Get the selected currency symbol
  const getCurrencySymbol = (currencyId) => {
    const currency = allCurrencies.find((c) => c.id === currencyId);
    return currency ? currency.symbol : "";
  };

  // Get appropriate list of currencies based on conversion mode
  const getFromCurrencyOptions = () => {
    return conversionMode === "crypto-to-fiat" ? cryptoList : fiatList;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      {/* Marketing Banner */}
      <MKTBanner />

      {/* Main Content */}
      <div className="container mx-auto p-4 flex-1">
        <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
          3i CryptoRate - Crypto Quotation & Converter
        </h1>

        {/* Market Overview */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Market Overview</h2>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">
                Last updated: {lastUpdated || "N/A"}
              </span>
              <button
                onClick={handleRefresh}
                className="p-2 text-violet-600 hover:bg-violet-50 rounded-full"
                disabled={loading}
              >
                <RefreshCw
                  size={18}
                  className={loading ? "animate-spin" : ""}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {cryptoList.map((crypto) => (
              <div
                key={crypto.id}
                className="border rounded-lg p-3 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-2">
                  <img
                    src={crypto.icon}
                    alt={crypto.name}
                    className="w-6 h-6 mr-2 rounded-full"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="font-medium">{crypto.symbol}</span>
                </div>
                <div className="text-xl font-bold">
                  {loading ? (
                    <div className="h-6 bg-gray-200 animate-pulse rounded w-24"></div>
                  ) : (
                    `$${formatNumber(cryptoData[crypto.id]?.usd || 0)}`
                  )}
                </div>
                <div className="text-xs text-gray-500">{crypto.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Converter */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Currency Converter</h2>
            <button
              onClick={toggleConversionMode}
              className="flex items-center bg-violet-100 hover:bg-violet-200 text-violet-800 px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowDownUp size={16} className="mr-2" />
              {conversionMode === "crypto-to-fiat"
                ? "Switch to Fiat → Crypto"
                : "Switch to Crypto → Fiat"}
            </button>
          </div>

          {/* Conversion Mode Display */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-gray-700 font-medium">
              Mode:{" "}
              {conversionMode === "crypto-to-fiat"
                ? "Cryptocurrency to Fiat"
                : "Fiat to Cryptocurrency"}
            </p>
          </div>

          {/* From Currency */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From
            </label>
            <div className="flex flex-col sm:flex-row gap-4">
              <NumericFormat
                value={amount}
                onValueChange={(values) => setAmount(values.floatValue || 0)}
                thousandSeparator=","
                decimalSeparator="."
                allowNegative={false}
                decimalScale={
                  conversionMode === "crypto-to-fiat" &&
                  fromCurrency === "bitcoin"
                    ? 8
                    : conversionMode === "crypto-to-fiat" &&
                      fromCurrency === "ethereum"
                    ? 6
                    : 4
                }
                className="w-full sm:flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />

              <select
                value={fromCurrency}
                onChange={(e) => setFromCurrency(e.target.value)}
                className="w-full sm:w-40 p-3 border rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white"
              >
                {getFromCurrencyOptions().map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.symbol} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Separator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex-grow my-6 border-t border-gray-200"></div>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>

          {/* Results Display - Changes based on conversion mode */}
          {conversionMode === "crypto-to-fiat" ? (
            <>
              {/* Fiat Currency Values - When converting from crypto */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Equivalent Fiat Values
                </label>
                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-1 divide-y">
                    {fiatList.map((fiat) => (
                      <div
                        key={fiat.id}
                        className="p-4 flex justify-between items-center"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 mr-3 flex items-center justify-center">
                            <img
                              src={fiat.icon}
                              alt={fiat.name}
                              className="w-8 h-8 object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div>
                            <div className="font-medium">{fiat.name}</div>
                            <div className="text-xs text-gray-500">
                              {fiat.id.toUpperCase()}
                            </div>
                          </div>
                        </div>
                        <div className="text-xl font-semibold">
                          {loading ? (
                            <div className="h-6 bg-gray-200 animate-pulse rounded w-24"></div>
                          ) : (
                            `${fiat.symbol} ${formatNumber(
                              calculateConversion(fiat.id)
                            )}`
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Crypto Currency Values - When converting from crypto */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cryptocurrency Equivalents
                </label>
                <div className="bg-gray-50 border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cryptoList
                      .filter((crypto) => crypto.id !== fromCurrency)
                      .map((crypto) => (
                        <div
                          key={crypto.id}
                          className="bg-white rounded-lg border p-3 flex justify-between items-center"
                        >
                          <div className="flex items-center">
                            <img
                              src={crypto.icon}
                              alt={crypto.name}
                              className="w-8 h-8 mr-2 rounded-full"
                              loading="lazy"
                              decoding="async"
                            />
                            <span>{crypto.symbol}</span>
                          </div>
                          <div className="font-semibold">
                            {loading ? (
                              <div className="h-5 bg-gray-200 animate-pulse rounded w-20"></div>
                            ) : (
                              formatNumber(calculateConversion(crypto.id))
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Fiat-to-Crypto mode display */
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cryptocurrency Equivalents for {amount}{" "}
                {getCurrencySymbol(fromCurrency)}
              </label>
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="grid grid-cols-1 divide-y">
                  {cryptoList.map((crypto) => (
                    <div
                      key={crypto.id}
                      className="p-4 flex justify-between items-center"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 mr-3 flex items-center justify-center">
                          <img
                            src={crypto.icon}
                            alt={crypto.name}
                            className="w-8 h-8 object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div>
                          <div className="font-medium">{crypto.name}</div>
                          <div className="text-xs text-gray-500">
                            {crypto.symbol}
                          </div>
                        </div>
                      </div>
                      <div className="text-xl font-semibold">
                        {loading ? (
                          <div className="h-6 bg-gray-200 animate-pulse rounded w-24"></div>
                        ) : (
                          formatNumber(calculateConversion(crypto.id))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Rate Info */}
          <div className="bg-violet-50 p-4 rounded-lg">
            <div className="flex items-start">
              <Info
                size={20}
                className="text-violet-600 mr-2 mt-0.5 flex-shrink-0"
              />
              <div>
                <p className="text-sm text-gray-700 font-medium mb-1">
                  Exchange Rates
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                  {conversionMode === "crypto-to-fiat"
                    ? // Crypto to fiat rates
                      fiatList.map((fiat) => (
                        <p key={fiat.id} className="text-sm text-gray-600">
                          1 {getCurrencySymbol(fromCurrency)} ={" "}
                          {formatNumber(calculateConversion(fiat.id) / amount)}{" "}
                          {fiat.symbol} ({fiat.name})
                        </p>
                      ))
                    : // Fiat to crypto rates
                      cryptoList.map((crypto) => (
                        <p key={crypto.id} className="text-sm text-gray-600">
                          1 {getCurrencySymbol(fromCurrency)} ={" "}
                          {formatNumber(
                            calculateConversion(crypto.id) / amount
                          )}{" "}
                          {crypto.symbol} ({crypto.name})
                        </p>
                      ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Data provided by {dataSource} API. Actual rates may vary
                  slightly from those shown.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CoinValueWebsite;
